import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Avatar,
  Typography,
  Tooltip,
  Drawer,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  StopOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { usersAPI, loginLogsAPI, getImageUrl } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const FAIL_REASON_LABEL = {
  no_such_account: '账号不存在',
  wrong_password: '密码错误',
  deactivated: '账号已停用',
};

/** last_login 注册时会被默认写成当前时间，没有 IP 且几乎等于注册时间则视为从未登录 */
const hasRealLogin = (user) => {
  if (!user) return false;
  if (user.lastLoginIp) return true;
  if (!user.lastLogin) return false;
  if (!user.createdAt) return true;
  return Math.abs(dayjs(user.lastLogin).diff(dayjs(user.createdAt))) > 60 * 1000;
};

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [logsUser, setLogsUser] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [userLogs, setUserLogs] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, searchText, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
      };
      if (searchText) params.search = searchText;
      if (roleFilter) params.role = roleFilter;

      const response = await usersAPI.getAll(params);
      if (response.data.success) {
        setUsers(response.data.data.users);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (error) {
      message.error('获取用户列表失败');
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setCurrentUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      mobilePhone: user.mobilePhone,
      role: user.role,
      password: undefined,
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (!payload.password) {
        delete payload.password;
      }
      await usersAPI.update(currentUser.id, payload);
      message.success('用户信息更新成功');
      setEditModalVisible(false);
      form.resetFields();
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.message || '更新失败');
      console.error('Update user error:', error);
    }
  };

  const handleCreate = () => {
    setCurrentUser(null);
    createForm.resetFields();
    createForm.setFieldsValue({
      role: 'user',
      isActive: 'true'
    });
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async () => {
    try {
      const values = await createForm.validateFields();

      // Convert isActive string to boolean
      const submitData = {
        ...values,
        isActive: values.isActive === 'true' || values.isActive === true
      };

      await usersAPI.create(submitData);
      message.success('账户创建成功');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchUsers();
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = error.response.data.errors.map(err => err.msg || err.message).join(', ');
        message.error(errorMessages);
      } else {
        message.error(error.response?.data?.message || '创建失败');
      }
      console.error('Create user error:', error);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await usersAPI.toggleStatus(user.id);
      message.success(`用户已${user.isActive ? '禁用' : '启用'}`);
      fetchUsers();
    } catch (error) {
      message.error('操作失败');
      console.error('Toggle status error:', error);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await usersAPI.delete(userId);
      message.success('用户已删除');
      fetchUsers();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete user error:', error);
    }
  };

  const handleViewLogs = async (user) => {
    setLogsUser(user);
    setLogsDrawerOpen(true);
    setLogsLoading(true);
    try {
      const response = await loginLogsAPI.getByUser(user.id, { limit: 100 });
      if (response.data.success) {
        setUserLogs(response.data.data.logs || []);
      } else {
        setUserLogs([]);
      }
    } catch (error) {
      message.error('获取该客户登录记录失败');
      console.error('Fetch user login logs error:', error);
      setUserLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} src={getImageUrl(record.avatar)} />
          <div>
            <div>{record.username}</div>
            <small style={{ color: '#999' }}>{record.email}</small>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      render: (mobilePhone) => mobilePhone || '-',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'red' : role === 'vip' ? 'gold' : 'blue'}>
          {role === 'admin' ? '管理员' : role === 'vip' ? 'VIP' : '普通用户'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '最后登录',
      key: 'lastLogin',
      width: 170,
      render: (_, record) =>
        hasRealLogin(record) ? (
          <Text>{dayjs(record.lastLogin).format('YYYY-MM-DD HH:mm:ss')}</Text>
        ) : (
          <Text type="secondary">从未登录</Text>
        ),
    },
    {
      title: '登录 IP',
      dataIndex: 'lastLoginIp',
      key: 'lastLoginIp',
      width: 150,
      render: (ip) =>
        ip ? <Text copyable style={{ fontFamily: 'monospace' }}>{ip}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="查看登录记录">
            <Button type="link" icon={<HistoryOutlined />} onClick={() => handleViewLogs(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={record.isActive ? '禁用' : '启用'}>
            <Popconfirm
              title={`确定要${record.isActive ? '禁用' : '启用'}该用户吗？`}
              onConfirm={() => handleToggleStatus(record)}
            >
              <Button
                type="link"
                icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                danger={record.isActive}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm title="确定要删除该用户吗？" onConfirm={() => handleDelete(record.id)}>
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4}>用户管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新增账户
        </Button>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索用户名或邮箱"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={fetchUsers}
            style={{ width: 250 }}
          />
          <Select
            placeholder="筛选角色"
            value={roleFilter}
            onChange={setRoleFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="user">普通用户</Option>
            <Option value="vip">VIP会员</Option>
            <Option value="admin">管理员</Option>
          </Select>
          <Button onClick={fetchUsers}>搜索</Button>
        </Space>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1180 }}
          locale={{ emptyText: '暂无用户数据' }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="新增账户"
        open={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        okText="创建"
        cancelText="取消"
        width={600}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item 
            name="username" 
            label="用户名" 
            rules={[{ required: true, message: '请输入用户名' }, { min: 2, message: '用户名至少2位' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>
          <Form.Item 
            name="email" 
            label="邮箱" 
            rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入有效的邮箱地址' }]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item 
            name="password" 
            label="密码" 
            rules={[{ required: true, message: '请输入密码' }, { min: 6, message: '密码至少6位' }]}
          >
            <Input.Password placeholder="请输入密码（至少6位）" />
          </Form.Item>
          <Form.Item name="mobilePhone" label="手机号">
            <Input placeholder="请输入手机号（可选）" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true }]}>
            <Select>
              <Option value="user">普通用户</Option>
              <Option value="vip">VIP会员</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>
          <Form.Item name="isActive" label="状态">
            <Select>
              <Option value="true">正常</Option>
              <Option value="false">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={logsUser ? `${logsUser.username} 的登录记录` : '登录记录'}
        open={logsDrawerOpen}
        width={720}
        onClose={() => {
          setLogsDrawerOpen(false);
          setLogsUser(null);
          setUserLogs([]);
        }}
      >
        {logsUser && (
          <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
            <Text type="secondary">邮箱：{logsUser.email || '—'}</Text>
            <Text type="secondary">
              最近登录：{hasRealLogin(logsUser) ? dayjs(logsUser.lastLogin).format('YYYY-MM-DD HH:mm:ss') : '从未登录'}
              {logsUser.lastLoginIp ? ` · ${logsUser.lastLoginIp}` : ''}
            </Text>
          </Space>
        )}
        <Table
          rowKey="id"
          size="small"
          loading={logsLoading}
          dataSource={userLogs}
          locale={{ emptyText: '该客户暂无登录记录（功能上线后产生的登录才会入库）' }}
          pagination={false}
          columns={[
            {
              title: '登录时间',
              dataIndex: 'createdAt',
              width: 170,
              render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
            },
            {
              title: 'IP 地址',
              dataIndex: 'ip',
              width: 150,
              render: (v) => (v ? <Text copyable style={{ fontFamily: 'monospace' }}>{v}</Text> : '—'),
            },
            {
              title: '结果',
              dataIndex: 'status',
              width: 120,
              render: (v, r) =>
                v === 'success' ? (
                  <Tag color="green">成功</Tag>
                ) : (
                  <Tag color="red">{FAIL_REASON_LABEL[r.failReason] || '失败'}</Tag>
                ),
            },
          ]}
        />
      </Drawer>

      {/* Edit Modal */}
      <Modal
        title="编辑用户"
        open={editModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="mobilePhone" label="手机号">
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="新密码"
            rules={[{ min: 6, message: '密码至少6位' }]}
            extra="留空则不修改密码"
          >
            <Input.Password placeholder="留空不修改" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="role" label="角色">
            <Select>
              <Option value="user">普通用户</Option>
              <Option value="vip">VIP会员</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default Users;
